"use client";

import { useState } from "react";
import {
  Plus,
  X,
  GripVertical,
  Pencil,
  Check,
  Lock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "@/lib/utils";
import type { TemplatePage } from "@/types";

interface PageTabsProps {
  pages: TemplatePage[];
  activePageIndex: number;
  onPageSelect: (index: number) => void;
  onPageAdd: () => void;
  onPageRemove: (index: number) => void;
  onPageRename: (index: number, name: string) => void;
  onPagesReorder: (pages: TemplatePage[]) => void;
  readOnly?: boolean;
  ownPagesStartIndex?: number; // Index from which own pages start (pages before are inherited)
}

interface SortableTabProps {
  page: TemplatePage;
  isActive: boolean;
  onSelect: () => void;
  onRemove: () => void;
  onRename: (name: string) => void;
  canRemove: boolean;
  readOnly?: boolean;
  isInherited?: boolean;
}

function SortableTab({
  page,
  isActive,
  onSelect,
  onRemove,
  onRename,
  canRemove,
  readOnly = false,
  isInherited = false,
}: SortableTabProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(page.name);

  // Inherited pages are always read-only and can't be dragged
  const isPageReadOnly = readOnly || isInherited;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: page.index.toString(), disabled: isPageReadOnly });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleStartEdit = (e: React.MouseEvent) => {
    if (isPageReadOnly) return;
    e.stopPropagation();
    setEditName(page.name);
    setIsEditing(true);
  };

  const handleSaveEdit = () => {
    if (editName.trim()) {
      onRename(editName.trim());
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSaveEdit();
    } else if (e.key === "Escape") {
      setIsEditing(false);
    }
  };

  const tabContent = (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-1 px-3 py-2 border-b-2 cursor-pointer transition-colors group",
        isActive
          ? "border-primary bg-background text-foreground"
          : "border-transparent hover:bg-muted/50 text-muted-foreground hover:text-foreground",
        isDragging && "opacity-50 z-50",
        isInherited && "bg-blue-50/50 border-blue-200"
      )}
      onClick={onSelect}
    >
      {/* Lock icon for inherited pages */}
      {isInherited && (
        <Lock className="h-3 w-3 text-blue-500 shrink-0" />
      )}

      {!isPageReadOnly && (
        <button
          className="cursor-grab opacity-0 group-hover:opacity-100 transition-opacity"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-3 w-3 text-muted-foreground" />
        </button>
      )}
      
      {isEditing ? (
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <Input
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleSaveEdit}
            className="h-6 w-24 text-xs px-1"
            autoFocus
          />
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5"
            onClick={handleSaveEdit}
          >
            <Check className="h-3 w-3" />
          </Button>
        </div>
      ) : (
        <>
          <span className={cn(
            "text-sm font-medium whitespace-nowrap",
            isInherited && "text-blue-700"
          )}>
            {page.name}
          </span>
          {!isPageReadOnly && (
            <button
              className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-muted rounded"
              onClick={handleStartEdit}
            >
              <Pencil className="h-3 w-3 text-muted-foreground" />
            </button>
          )}
        </>
      )}
      
      {!isPageReadOnly && canRemove && (
        <button
          className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-destructive/20 rounded ml-1"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          <X className="h-3 w-3 text-destructive" />
        </button>
      )}
    </div>
  );

  // Wrap inherited tabs with a tooltip
  if (isInherited) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {tabContent}
          </TooltipTrigger>
          <TooltipContent>
            <p>Page heritee du template parent (lecture seule)</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return tabContent;
}

export function PageTabs({
  pages,
  activePageIndex,
  onPageSelect,
  onPageAdd,
  onPageRemove,
  onPageRename,
  onPagesReorder,
  readOnly = false,
  ownPagesStartIndex = 0,
}: PageTabsProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Get only own pages (non-inherited) for drag and drop
  const ownPages = pages.filter((_, idx) => idx >= ownPagesStartIndex);
  const ownPagesCount = ownPages.length;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = pages.findIndex((p) => p.index.toString() === active.id);
      const newIndex = pages.findIndex((p) => p.index.toString() === over.id);

      // Don't allow dragging inherited pages or dragging into inherited position
      if (oldIndex < ownPagesStartIndex || newIndex < ownPagesStartIndex) {
        return;
      }

      const reorderedPages = arrayMove(pages, oldIndex, newIndex).map((page, idx) => ({
        ...page,
        index: idx,
      }));

      onPagesReorder(reorderedPages);
    }
  };

  return (
    <div className="flex items-center border-b bg-muted/30 overflow-x-auto">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={pages.map((p) => p.index.toString())}
          strategy={horizontalListSortingStrategy}
        >
          {pages.map((page, idx) => {
            const isInherited = idx < ownPagesStartIndex;
            // For inherited pages, can't remove. For own pages, can remove if there are
            // other own pages OR if there are inherited pages to fall back on
            const canRemove = !isInherited && (ownPagesCount > 1 || ownPagesStartIndex > 0);
            
            return (
              <SortableTab
                key={page.index}
                page={page}
                isActive={page.index === activePageIndex}
                onSelect={() => onPageSelect(page.index)}
                onRemove={() => onPageRemove(page.index)}
                onRename={(name) => onPageRename(page.index, name)}
                canRemove={canRemove}
                readOnly={readOnly}
                isInherited={isInherited}
              />
            );
          })}
        </SortableContext>
      </DndContext>

      {!readOnly && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 px-2 ml-1"
          onClick={onPageAdd}
          title="Ajouter une page"
        >
          <Plus className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
